// AIBusinessInsightsScreen.js — Dedicated AI Business Insights for JobEng.
// Self-contained: loads real company data from the DB and sends it to the
// /api/ai proxy directly. No dependency on AIBrainService / agents, so the
// AI always receives real numbers and never returns an empty-context answer.
//
// Register in App.js:
//   import AIBusinessInsightsScreen from './app/screens/business/erp/AIBusinessInsightsScreen';
//   <Stack.Screen name="AIBusinessInsights" component={AIBusinessInsightsScreen} />

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput,
  ActivityIndicator, RefreshControl, SafeAreaView, StatusBar, Platform,
  KeyboardAvoidingView,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── Connection ──────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const T = {
  blue: '#0e4e96', canary: '#fdb913', mint: '#19abb5',
  bg: '#f8fafc', surface: '#ffffff', text: '#0f172a', sub: '#475569',
  line: '#e2e8f0', success: '#10b981', warning: '#f59e0b',
  danger: '#ef4444', info: '#3b82f6', purple: '#8b5cf6', pink: '#ec4899',
};

const getHeaders = async () => {
  const token = (await AsyncStorage.getItem('auth_token')) || (await AsyncStorage.getItem('userToken')) || '';
  return {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};
const getCid = async () => {
  const raw = (await AsyncStorage.getItem('company_id')) || '0';
  return parseInt(raw, 10) || 0;
};
const num = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? n : d; };
const fmtMoney = (n) => {
  const v = num(n);
  if (Math.abs(v) >= 1e6) return `£${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `£${(v / 1e3).toFixed(1)}K`;
  return `£${v.toFixed(0)}`;
};

// ─── Insight categories (each sends a focused prompt) ─────────────────────────
const CATEGORIES = [
  { id: 'priorities', label: 'Top Priorities', icon: 'flag-checkered', color: T.blue,
    prompt: 'Based on the business data above, what are the 3 most important things this company should prioritise this week? Be specific and reference the actual numbers.' },
  { id: 'revenue', label: 'Revenue Analysis', icon: 'trending-up', color: T.success,
    prompt: 'Analyse this company\'s revenue and invoicing data. Identify trends, outstanding amounts, and one concrete recommendation to improve cash collection.' },
  { id: 'cashflow', label: 'Cash Flow', icon: 'cash-multiple', color: T.mint,
    prompt: 'Review income (invoices) versus expenses. Comment on the cash position and flag any risks. Give one practical action.' },
  { id: 'team', label: 'Team & HR', icon: 'account-group', color: T.purple,
    prompt: 'Analyse the workforce data (headcount, roles). Comment on team composition and suggest one HR or staffing focus area.' },
  { id: 'projects', label: 'Projects', icon: 'briefcase-check', color: T.warning,
    prompt: 'Review the active projects. Comment on workload and delivery focus, and suggest how to prioritise across them.' },
  { id: 'growth', label: 'Growth Ideas', icon: 'rocket-launch', color: T.pink,
    prompt: 'Based on the client base and current data, suggest 2 realistic growth opportunities for this UK business.' },
];

const AIBusinessInsightsScreen = ({ navigation }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [metrics, setMetrics] = useState({
    revenue: 0, expenses: 0, profit: 0, invoicesOutstanding: 0,
    employees: 0, clients: 0, projects: 0, products: 0,
  });
  const [rawContext, setRawContext] = useState('');     // text block sent to the AI
  const [activeCategory, setActiveCategory] = useState(null);
  const [insight, setInsight] = useState('');           // AI response text
  const [aiLoading, setAiLoading] = useState(false);
  const [question, setQuestion] = useState('');
  const [chat, setChat] = useState([]);                 // {role, text}
  const scrollRef = useRef(null);

  // ─── Load real company data ────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    try {
      const cid = await getCid();
      const h = await getHeaders();

      // Company name
      try {
        const cp = await AsyncStorage.getItem('company_profile');
        if (cp) setCompanyName(JSON.parse(cp)?.name || '');
      } catch {}

      const q = (table) =>
        fetch(`${API_BASE}/api/data/${table}?company_id=${cid}&limit=200`, { headers: h })
          .then(r => r.json()).then(d => (d.success && Array.isArray(d.data)) ? d.data : []).catch(() => []);

      const [employees, clients, projects, invoices, expenses, products] = await Promise.all([
        q('employees'), q('clients'), q('projects'),
        q('financial_invoices'), q('financial_expenses'), q('company_products'),
      ]);

      // Compute metrics
      const revenue = invoices.reduce((s, r) => s + num(r.amount ?? r.total ?? r.value), 0);
      const expTotal = expenses.reduce((s, r) => s + num(r.amount ?? r.total ?? r.value), 0);
      const outstanding = invoices
        .filter(r => String(r.status || '').toLowerCase() !== 'paid')
        .reduce((s, r) => s + num(r.amount ?? r.total ?? r.value), 0);

      setMetrics({
        revenue, expenses: expTotal, profit: revenue - expTotal,
        invoicesOutstanding: outstanding,
        employees: employees.length, clients: clients.length,
        projects: projects.length, products: products.length,
      });

      // Build the text context block that gets sent to the AI.
      const lines = [];
      lines.push(`Company: ${companyName || 'this company'} (ID ${cid})`);
      lines.push(`Employees: ${employees.length}`);
      lines.push(`Clients: ${clients.length}`);
      lines.push(`Active projects: ${projects.length}`);
      lines.push(`Products/services: ${products.length}`);
      lines.push(`Total invoiced revenue: ${fmtMoney(revenue)} across ${invoices.length} invoices`);
      lines.push(`Outstanding (unpaid) invoices: ${fmtMoney(outstanding)}`);
      lines.push(`Total expenses: ${fmtMoney(expTotal)} across ${expenses.length} records`);
      lines.push(`Net position: ${fmtMoney(revenue - expTotal)}`);
      // A few sample records for richer grounding
      if (clients.length) {
        lines.push(`Sample clients: ${clients.slice(0, 5).map(c => c.name || c.company_name || c.client_name).filter(Boolean).join(', ')}`);
      }
      if (projects.length) {
        lines.push(`Sample projects: ${projects.slice(0, 5).map(p => p.name || p.project_name || p.title).filter(Boolean).join(', ')}`);
      }
      setRawContext(lines.join('\n'));
    } catch (e) {
      console.warn('[AIBusinessInsights] load failed:', e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyName]);

  useEffect(() => { loadData(); }, []);

  const onRefresh = () => { setRefreshing(true); loadData(); };

  // ─── Call the AI proxy with real context ───────────────────────────────────
  const runInsight = async (promptText, label) => {
    if (aiLoading) return;
    setAiLoading(true);
    setActiveCategory(label || null);
    setInsight('');
    try {
      const h = await getHeaders();
      const fullPrompt =
        `You are an expert UK business analyst for JobEng ERP. ` +
        `Here is the company's current business data:\n\n${rawContext}\n\n` +
        `${promptText}\n\n` +
        `Respond concisely with specific references to the numbers above. Use short paragraphs or bullet points.`;

      const res = await fetch(`${API_BASE}/api/ai`, {
        method: 'POST',
        headers: h,
        body: JSON.stringify({ prompt: fullPrompt, max_tokens: 700, temperature: 0.7 }),
      });
      const data = await res.json();
      const text = data?.text || data?.content || data?.choices?.[0]?.message?.content || '';
      if (!res.ok || !text) throw new Error(data?.error || `HTTP ${res.status}`);
      setInsight(text);
    } catch (e) {
      setInsight(`Could not generate insight right now (${e.message}). Pull down to refresh your data and try again.`);
    } finally {
      setAiLoading(false);
    }
  };

  // ─── Free-form chat ─────────────────────────────────────────────────────────
  const sendQuestion = async () => {
    const q = question.trim();
    if (!q || aiLoading) return;
    setQuestion('');
    setChat(prev => [...prev, { role: 'user', text: q }]);
    setAiLoading(true);
    try {
      const h = await getHeaders();
      const fullPrompt =
        `You are an expert UK business analyst for JobEng ERP. ` +
        `Here is the company's current business data:\n\n${rawContext}\n\n` +
        `User question: ${q}\n\n` +
        `Answer concisely and specifically using the data above.`;
      const res = await fetch(`${API_BASE}/api/ai`, {
        method: 'POST',
        headers: h,
        body: JSON.stringify({ prompt: fullPrompt, max_tokens: 700, temperature: 0.7 }),
      });
      const data = await res.json();
      const text = data?.text || data?.content || data?.choices?.[0]?.message?.content || '';
      setChat(prev => [...prev, { role: 'assistant', text: text || 'No response. Try again.' }]);
    } catch (e) {
      setChat(prev => [...prev, { role: 'assistant', text: `Error: ${e.message}` }]);
    } finally {
      setAiLoading(false);
      setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 200);
    }
  };

  // ─── UI ──────────────────────────────────────────────────────────────────────
  const metricCards = [
    { label: 'Revenue', value: fmtMoney(metrics.revenue), icon: 'trending-up', color: T.success },
    { label: 'Expenses', value: fmtMoney(metrics.expenses), icon: 'trending-down', color: T.danger },
    { label: 'Net', value: fmtMoney(metrics.profit), icon: 'scale-balance', color: metrics.profit >= 0 ? T.success : T.danger },
    { label: 'Outstanding', value: fmtMoney(metrics.invoicesOutstanding), icon: 'clock-alert', color: T.warning },
    { label: 'Employees', value: String(metrics.employees), icon: 'account-group', color: T.blue },
    { label: 'Clients', value: String(metrics.clients), icon: 'handshake', color: T.mint },
    { label: 'Projects', value: String(metrics.projects), icon: 'briefcase', color: T.purple },
    { label: 'Products', value: String(metrics.products), icon: 'package-variant', color: T.pink },
  ];

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <ActivityIndicator size="large" color={T.blue} />
        <Text style={{ color: T.sub, marginTop: 12 }}>Loading your business data…</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" />
      <LinearGradient colors={[T.blue, '#1565c0']} style={styles.header}>
        <View style={styles.headerRow}>
          {navigation?.goBack && (
            <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
              <MaterialCommunityIcons name="arrow-left" size={22} color="#fff" />
            </TouchableOpacity>
          )}
          <View style={{ flex: 1 }}>
            <Text style={styles.headerTitle}>AI Business Insights</Text>
            <Text style={styles.headerSub}>{companyName || 'Your business'} · live data</Text>
          </View>
          <MaterialCommunityIcons name="brain" size={26} color={T.canary} />
        </View>
      </LinearGradient>

      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
      >
        <ScrollView
          ref={scrollRef}
          contentContainerStyle={{ padding: 16, paddingBottom: 40 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        >
          {/* Metrics grid */}
          <Text style={styles.sectionTitle}>Live Metrics</Text>
          <View style={styles.metricsGrid}>
            {metricCards.map(m => (
              <View key={m.label} style={styles.metricCard}>
                <MaterialCommunityIcons name={m.icon} size={20} color={m.color} />
                <Text style={[styles.metricValue, { color: m.color }]}>{m.value}</Text>
                <Text style={styles.metricLabel}>{m.label}</Text>
              </View>
            ))}
          </View>

          {/* Insight categories */}
          <Text style={styles.sectionTitle}>Generate Insight</Text>
          <View style={styles.catGrid}>
            {CATEGORIES.map(c => (
              <TouchableOpacity
                key={c.id}
                style={[styles.catCard, activeCategory === c.label && { borderColor: c.color, borderWidth: 2 }]}
                onPress={() => runInsight(c.prompt, c.label)}
                disabled={aiLoading}
              >
                <View style={[styles.catIcon, { backgroundColor: c.color + '18' }]}>
                  <MaterialCommunityIcons name={c.icon} size={20} color={c.color} />
                </View>
                <Text style={styles.catLabel}>{c.label}</Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* AI insight output */}
          {(aiLoading || insight) ? (
            <View style={styles.insightBox}>
              <View style={styles.insightHeader}>
                <MaterialCommunityIcons name="lightbulb-on" size={18} color={T.canary} />
                <Text style={styles.insightTitle}>{activeCategory || 'Insight'}</Text>
              </View>
              {aiLoading && !insight ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8 }}>
                  <ActivityIndicator size="small" color={T.blue} />
                  <Text style={{ color: T.sub }}>Analysing your data…</Text>
                </View>
              ) : (
                <Text style={styles.insightText}>{insight}</Text>
              )}
            </View>
          ) : null}

          {/* Free-form chat history */}
          {chat.length > 0 && (
            <>
              <Text style={styles.sectionTitle}>Conversation</Text>
              {chat.map((m, i) => (
                <View key={i} style={[styles.bubble, m.role === 'user' ? styles.bubbleUser : styles.bubbleAI]}>
                  <Text style={[styles.bubbleText, m.role === 'user' && { color: '#fff' }]}>{m.text}</Text>
                </View>
              ))}
              {aiLoading && chat[chat.length - 1]?.role === 'user' && (
                <View style={[styles.bubble, styles.bubbleAI]}>
                  <ActivityIndicator size="small" color={T.blue} />
                </View>
              )}
            </>
          )}
        </ScrollView>

        {/* Ask box */}
        <View style={styles.askBar}>
          <TextInput
            style={styles.askInput}
            value={question}
            onChangeText={setQuestion}
            placeholder="Ask anything about your business…"
            placeholderTextColor={T.sub}
            multiline
          />
          <TouchableOpacity
            style={[styles.askSend, { backgroundColor: question.trim() && !aiLoading ? T.blue : T.line }]}
            onPress={sendQuestion}
            disabled={!question.trim() || aiLoading}
          >
            <MaterialCommunityIcons name="send" size={18} color={question.trim() && !aiLoading ? '#fff' : T.sub} />
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: T.bg },
  header: { paddingTop: Platform.OS === 'ios' ? 8 : 36, paddingBottom: 18, paddingHorizontal: 16 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  backBtn: { width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.15)', alignItems: 'center', justifyContent: 'center' },
  headerTitle: { color: '#fff', fontSize: 20, fontWeight: '900' },
  headerSub: { color: 'rgba(255,255,255,0.85)', fontSize: 12, marginTop: 2 },
  sectionTitle: { fontSize: 15, fontWeight: '800', color: T.text, marginTop: 18, marginBottom: 10 },
  metricsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  metricCard: { width: '23%', minWidth: 76, backgroundColor: T.surface, borderRadius: 12, padding: 10, alignItems: 'flex-start', borderWidth: 1, borderColor: T.line },
  metricValue: { fontSize: 15, fontWeight: '900', marginTop: 6 },
  metricLabel: { fontSize: 10, color: T.sub, marginTop: 2 },
  catGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  catCard: { width: '31%', backgroundColor: T.surface, borderRadius: 12, padding: 12, alignItems: 'center', borderWidth: 1, borderColor: T.line },
  catIcon: { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center', marginBottom: 8 },
  catLabel: { fontSize: 11, fontWeight: '700', color: T.text, textAlign: 'center' },
  insightBox: { backgroundColor: T.surface, borderRadius: 14, padding: 16, marginTop: 16, borderWidth: 1, borderColor: T.line },
  insightHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 10 },
  insightTitle: { fontSize: 14, fontWeight: '800', color: T.text },
  insightText: { fontSize: 14, lineHeight: 21, color: T.text },
  bubble: { maxWidth: '85%', padding: 12, borderRadius: 14, marginBottom: 8 },
  bubbleUser: { alignSelf: 'flex-end', backgroundColor: T.blue, borderBottomRightRadius: 4 },
  bubbleAI: { alignSelf: 'flex-start', backgroundColor: T.surface, borderWidth: 1, borderColor: T.line, borderBottomLeftRadius: 4 },
  bubbleText: { fontSize: 14, lineHeight: 20, color: T.text },
  askBar: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, padding: 12, backgroundColor: T.surface, borderTopWidth: 1, borderTopColor: T.line },
  askInput: { flex: 1, maxHeight: 100, backgroundColor: T.bg, borderRadius: 20, paddingHorizontal: 16, paddingVertical: 10, fontSize: 14, color: T.text, borderWidth: 1, borderColor: T.line },
  askSend: { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center' },
});

export default AIBusinessInsightsScreen;
